const invalid = (value: string): boolean => Number.isNaN(new Date(value).getTime());

export const formatDateTime = (value: string): string => {
  if (invalid(value)) {
    return value;
  }
  return new Date(value).toLocaleString("pt-PT");
};

export const formatDate = (value: string): string => {
  if (invalid(value)) {
    return value;
  }
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/u.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);
  if (Number.isNaN(dateOnly.getTime())) {
    return value;
  }
  return dateOnly.toLocaleDateString("pt-PT", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

export const greetingForHour = (hour: number): string => {
  if (hour < 12) {
    return "Bom dia";
  }
  if (hour < 18) {
    return "Boa tarde";
  }
  return "Boa noite";
};

const startOfLocalDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const MS_PER_DAY = 86_400_000;

/**
 * Relative calendar day in pt-PT. Used for timeline grouping — not a new event store.
 */
export const relativeDayLabel = (value: string, now: Date): string => {
  if (invalid(value)) {
    return value;
  }
  const date = /^\d{4}-\d{2}-\d{2}$/u.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const diffDays = Math.round((startOfLocalDay(now) - startOfLocalDay(date)) / MS_PER_DAY);
  if (diffDays === 0) {
    return "Hoje";
  }
  if (diffDays === 1) {
    return "Ontem";
  }
  if (diffDays > 1 && diffDays < 30) {
    return `Há ${String(diffDays)} dias`;
  }
  if (diffDays === -1) {
    return "Amanhã";
  }
  return formatDate(value);
};

/**
 * Relative day when recent; otherwise absolute date. Always includes absolute when relative.
 * Real updatedAt only — not an activity feed.
 */
export const formatChangeWhen = (value: string, now: Date = new Date()): string => {
  if (invalid(value)) {
    return value;
  }
  const relative = relativeDayLabel(value, now);
  const absolute = formatDate(value);
  if (relative === absolute) {
    return absolute;
  }
  return `${relative} · ${absolute}`;
};

/**
 * Relative-only activity label for the narrative block ("Há 2 dias", "Ontem").
 * Falls back to absolute date when older than the relative window.
 */
export const formatRelativeActivity = (value: string, now: Date = new Date()): string => {
  if (invalid(value)) {
    return value;
  }
  return relativeDayLabel(value, now);
};

/** Display a country code when present. PT → Portugal. Missing stays null (caller shows placeholder). */
export const formatCountry = (countryCode: string | null): string | null => {
  if (countryCode === null) {
    return null;
  }
  const code = countryCode.trim().toUpperCase();
  if (code.length === 0) {
    return null;
  }
  if (code === "PT") {
    return "Portugal";
  }
  return code;
};
